import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { RxCollection } from 'rxdb';
import { OfflineStateService } from './offline-state.service';
import { TerminalIdentityService } from '@stockflow/offline-core';
import { API_CONFIG } from '../../config/api.config';
import { StockFlowDatabase } from '../rxdb-database';
import { PermissionService } from '../../../features/security/services/permission.service';
import { hydratationPermise } from './hydratation-permise';

/**
 * Configuration for each collection to hydrate.
 * Maps a RxDB collection name to the API endpoint that returns the full list.
 */
interface HydrationTarget {
  collection: keyof StockFlowDatabase['collections'];
  url: string;
  /**
   * Droit exige pour que cette collection descende sur le poste.
   *
   * Absent = donnee de reference, que toute session doit connaitre pour
   * afficher quoi que ce soit et qui ne dit rien de l'activite de
   * l'entreprise. Voir `hydratation-permise.ts`.
   */
  permission?: string;
  /** Field used as primary key when upserting (default: 'id') */
  primaryKey?: string;
  /** Transform API response before upserting (e.g. extract .data from paginated response) */
  transform?: (response: unknown) => unknown[];
}

/** Transform reference data: prefix ID with type to avoid collisions across ref-data types */
function refDataTransform(response: unknown, type: string): unknown[] {
  const arr = Array.isArray(response) ? response : [];
  return arr.map((d: Record<string, unknown>) => ({
    ...d,
    id: `${type}_${d['id']}`,
    type,
  }));
}

const HYDRATION_TARGETS: HydrationTarget[] = [
  // ─── Inventory ───
  { collection: 'products', url: `${API_CONFIG.inventoryService}/products`, permission: 'PRODUCT:READ' },
  { collection: 'stocks', url: `${API_CONFIG.inventoryService}/stocks`, permission: 'STOCK_SUMMARY:READ' },
  { collection: 'warehouses', url: `${API_CONFIG.inventoryService}/warehouses`, permission: 'WAREHOUSE:READ' },
  // ─── Partners ───
  { collection: 'customers', url: `${API_CONFIG.partnersService}/customers`, permission: 'CUSTOMER:READ' },
  { collection: 'suppliers', url: `${API_CONFIG.partnersService}/suppliers`, permission: 'SUPPLIER:READ' },
  { collection: 'sales_orders', url: `${API_CONFIG.partnersService}/sales-orders`, permission: 'SALES_ORDER:READ' },
  { collection: 'purchase_orders', url: `${API_CONFIG.partnersService}/purchase-orders`, permission: 'PURCHASE_ORDER:READ' },
  { collection: 'pricing_rules', url: `${API_CONFIG.partnersService}/pricing-rules`, permission: 'PRICING_RULES:READ' },
  { collection: 'tax_rules', url: `${API_CONFIG.partnersService}/tax-rules`, permission: 'TAXES:READ' },
  { collection: 'invoices', url: `${API_CONFIG.partnersService}/invoices`, permission: 'INVOICE:READ' },
  { collection: 'currency_rates', url: `${API_CONFIG.partnersService}/currency-rates` },
  // ─── Accounting ───
  { collection: 'chart_of_accounts', url: `${API_CONFIG.accountingService}/chart-of-accounts`, permission: 'CHART_OF_ACCOUNTS:READ' },
  { collection: 'journal_entries', url: `${API_CONFIG.accountingService}/journal-entries`, permission: 'JOURNAL_ENTRY:READ' },
  { collection: 'fiscal_years', url: `${API_CONFIG.accountingService}/fiscal-years`, permission: 'FISCAL_YEAR:READ' },
  { collection: 'pay_runs', url: `${API_CONFIG.accountingService}/pay-runs`, permission: 'PAYROLL:READ' },
  // ─── HR ───
  { collection: 'employees', url: `${API_CONFIG.hrService}/employees`, permission: 'EMPLOYEE:READ' },
  { collection: 'contracts', url: `${API_CONFIG.hrService}/contracts`, permission: 'EMPLOYEE:READ' },
  { collection: 'leave_requests', url: `${API_CONFIG.hrService}/leave-requests`, permission: 'LEAVE:READ' },
  { collection: 'departments', url: `${API_CONFIG.hrService}/departments`, permission: 'HR_SETTINGS:READ' },
  // ─── Users ───
  { collection: 'users', url: `${API_CONFIG.userManagement}/users`, permission: 'USER:READ' },
  { collection: 'roles', url: `${API_CONFIG.userManagement}/roles`, permission: 'ROLE:READ' },
  // ─── Reference Data ───
  { collection: 'product_families', url: `${API_CONFIG.referenceData}/ref-data/product-families` },
  { collection: 'product_types', url: `${API_CONFIG.referenceData}/ref-data/product-types` },
  { collection: 'variants', url: `${API_CONFIG.referenceData}/ref-data/variants` },
  { collection: 'reference_data', url: `${API_CONFIG.referenceData}/ref-data/units`, transform: (r) => refDataTransform(r, 'units') },
  { collection: 'reference_data', url: `${API_CONFIG.referenceData}/ref-data/payment-methods`, transform: (r) => refDataTransform(r, 'payment-methods') },
  { collection: 'reference_data', url: `${API_CONFIG.referenceData}/ref-data/delivery-methods`, transform: (r) => refDataTransform(r, 'delivery-methods') },
  { collection: 'reference_data', url: `${API_CONFIG.referenceData}/ref-data/client-types`, transform: (r) => refDataTransform(r, 'client-types') },
  // Taxes (base definitions with rate, code, calculationOrder) — needed by offline tax engine
  { collection: 'reference_data', url: `${API_CONFIG.partnersService}/api/taxes`, transform: (r) => refDataTransform(r, 'taxes') },
];

export interface HydrationResult {
  collection: string;
  recordCount: number;
  status: 'SUCCESS' | 'FAILED';
  error?: string;
  durationMs: number;
}

@Injectable({ providedIn: 'root' })
export class HydrationService {
  private _hydrating = false;

  constructor(
    private http: HttpClient,
    private offlineState: OfflineStateService,
    private terminalIdentity: TerminalIdentityService,
    private permissions: PermissionService,
  ) {}

  /**
   * Les collections que CET usager a le droit de descendre sur son poste.
   *
   * Une donnee cachee mais presente en base locale se lit avec les outils du
   * navigateur, survit a la deconnexion, et part avec la machine : ne pas
   * l'afficher ne suffit pas, il faut ne pas la telecharger.
   */
  private ciblesPermises(): HydrationTarget[] {
    const effectif = this.permissions.userPermissions();
    return HYDRATION_TARGETS.filter((t) =>
      hydratationPermise(t.permission, effectif),
    );
  }

  get isHydrating(): boolean {
    return this._hydrating;
  }

  /**
   * Full hydration: pull all data from cloud APIs into RxDB.
   * Called on first Tauri launch or when sync_log is empty.
   */
  async hydrateAll(companyId: number): Promise<HydrationResult[]> {
    if (this._hydrating) return [];
    this._hydrating = true;

    try {
      const db = this.offlineState.getDb() ?? await this.offlineState.initDatabase(companyId);
      const results: HydrationResult[] = [];
      const cibles = this.ciblesPermises();
      const total = cibles.length;

      // Hydrate in batches of 4 to avoid overwhelming the network
      for (let i = 0; i < total; i += 4) {
        const batch = cibles.slice(i, i + 4);
        this.offlineState.setHydrationProgress(i, total, batch[0].collection as string);
        const batchResults = await Promise.allSettled(
          batch.map(target => this.hydrateCollection(db, target)),
        );
        for (const result of batchResults) {
          if (result.status === 'fulfilled') {
            results.push(result.value);
          }
        }
      }

      this.offlineState.setHydrationProgress(total, total, '');
      return results;
    } finally {
      this._hydrating = false;
    }
  }

  /**
   * Delta hydration: only pull records updated since last sync.
   * Falls back to full hydration if no sync_log entry exists.
   */
  async hydrateDelta(companyId: number): Promise<HydrationResult[]> {
    if (this._hydrating) return [];
    this._hydrating = true;

    try {
      const db = this.offlineState.getDb() ?? await this.offlineState.initDatabase(companyId);
      const results: HydrationResult[] = [];
      const cibles = this.ciblesPermises();
      const total = cibles.length;

      for (let i = 0; i < total; i += 4) {
        const batch = cibles.slice(i, i + 4);
        this.offlineState.setHydrationProgress(i, total, batch[0].collection as string);
        const batchResults = await Promise.allSettled(
          batch.map(async target => {
            const lastSync = await this.getLastSyncAt(db, target.collection as string);
            return this.hydrateCollection(db, target, lastSync ?? undefined);
          }),
        );
        for (const result of batchResults) {
          if (result.status === 'fulfilled') {
            results.push(result.value);
          }
        }
      }

      this.offlineState.setHydrationProgress(total, total, '');
      return results;
    } finally {
      this._hydrating = false;
    }
  }

  /**
   * Check if any hydration has been done before (any sync_log entries exist).
   */
  async needsFullHydration(companyId: number): Promise<boolean> {
    const db = this.offlineState.getDb() ?? await this.offlineState.initDatabase(companyId);
    const logs = await db.sync_log.find({ selector: { direction: 'PULL', status: 'SUCCESS' } }).exec();
    return logs.length === 0;
  }

  private async hydrateCollection(
    db: StockFlowDatabase,
    target: HydrationTarget,
    since?: string,
  ): Promise<HydrationResult> {
    const start = Date.now();
    const collectionName = target.collection as string;

    try {
      let url = target.url;
      if (since) {
        const sep = url.includes('?') ? '&' : '?';
        url = `${url}${sep}updatedSince=${encodeURIComponent(since)}`;
      }

      const response = await firstValueFrom(this.http.get<unknown>(url));
      let records: unknown[];

      if (target.transform) {
        records = target.transform(response);
      } else if (Array.isArray(response)) {
        records = response;
      } else if (response && typeof response === 'object' && 'data' in response && Array.isArray((response as Record<string, unknown>)['data'])) {
        records = (response as Record<string, unknown>)['data'] as unknown[];
      } else {
        records = [];
      }

      const collection = db[target.collection] as RxCollection;
      if (!collection) {
        throw new Error(`Collection ${collectionName} not found in database`);
      }

      // Upsert each record — ensures both insert and update work
      let upserted = 0;
      for (const record of records) {
        try {
          const doc = this.normalizeRecord(record as Record<string, unknown>);
          await collection.upsert(doc);
          upserted++;
        } catch {
          // Skip individual record errors (e.g. schema validation)
        }
      }

      const durationMs = Date.now() - start;

      // Log sync entry
      await this.writeSyncLog(db, collectionName, upserted, 'SUCCESS', null, durationMs);

      return { collection: collectionName, recordCount: upserted, status: 'SUCCESS', durationMs };
    } catch (err: unknown) {
      const durationMs = Date.now() - start;
      const errorMsg = err instanceof Error ? err.message : String(err);

      await this.writeSyncLog(db, collectionName, 0, 'FAILED', errorMsg, durationMs).catch(() => {});

      return { collection: collectionName, recordCount: 0, status: 'FAILED', error: errorMsg, durationMs };
    }
  }

  /**
   * Normalize API record for RxDB: ensure `id` is a string (RxDB requires string PKs).
   */
  private normalizeRecord(record: Record<string, unknown>): Record<string, unknown> {
    const normalized = { ...record };
    if (normalized['id'] !== undefined && typeof normalized['id'] !== 'string') {
      normalized['id'] = String(normalized['id']);
    }
    return normalized;
  }

  private async getLastSyncAt(db: StockFlowDatabase, tableName: string): Promise<string | null> {
    const logs = await db.sync_log.find({
      selector: { tableName, direction: 'PULL', status: 'SUCCESS' },
    }).exec();

    if (logs.length === 0) return null;

    // Find the most recent syncedAt
    let latest = '';
    for (const log of logs) {
      const syncedAt = (log.toJSON() as { syncedAt: string }).syncedAt;
      if (syncedAt > latest) latest = syncedAt;
    }
    return latest || null;
  }

  private async writeSyncLog(
    db: StockFlowDatabase,
    tableName: string,
    recordCount: number,
    status: string,
    errorMessage: string | null,
    durationMs: number,
  ): Promise<void> {
    await db.sync_log.insert({
      id: crypto.randomUUID(),
      tableName,
      direction: 'PULL',
      recordCount,
      status,
      errorMessage,
      userId: null,
      // Voir ecart Y4 : le journal n'identifiait aucun poste.
      terminalId: this.terminalIdentity.code(),
      durationMs,
      syncedAt: new Date().toISOString(),
    });
  }
}
